import { useMemo } from "react";
import { Feature } from "geojson";

import { useFeaturesByLayer } from "fond/hooks/useFeaturesByLayer";

interface UseMultiLayerFeaturesOptions {
  /**
   * Optional mapping to override the layerId property on features.
   * If a layerKey has a mapping, features from that layer will have their
   * properties.layerId set to the mapped value instead of the original layerKey.
   * Layers not in the mapping will keep their original layerKey as the layerId.
   *
   * @example
   * {
   *   layerIdMapping: {
   *     "solve_hubs_t1": "hubs",
   *     "solve_hubs_t2": "hubs",
   *     // ugPoints not mapped, so keeps "ugPoints" as its layerId
   *   }
   * }
   */
  layerIdMapping?: Record<string, string>;
}

/**
 * Custom hook to fetch features for multiple layers.
 *
 * This hook fetches feature data for all specified layer keys and combines them
 * into a single array. Each feature is tagged with its layerId for later identification.
 *
 * Uses RTK Query's dispatch-based fetching to avoid React hooks limitations.
 *
 * @param versionId - The version ID to fetch layers from
 * @param layerKeys - Array of layer keys (e.g., ["demand", "leadins"])
 * @param options - Optional configuration for layer ID mapping
 * @returns Object containing combined features, loading state, and error state
 *
 * @example
 * // Basic usage - features get properties.layerId from their source layer
 * const { features, isLoading } = useMultiLayerFeatures(versionId, ["demand", "leadins"]);
 *
 * @example
 * // Map specific layers to different layer IDs
 * const { features, isLoading } = useMultiLayerFeatures(
 *   versionId,
 *   ["solve_hubs_t1", "solve_hubs_t2", "ugPoints"],
 *   {
 *     layerIdMapping: {
 *       "solve_hubs_t1": "hubs",
 *       "solve_hubs_t2": "hubs",
 *       // ugPoints not in mapping, keeps original ID
 *     }
 *   }
 * );
 */
export const useMultiLayerFeatures = (
  versionId: string,
  layerKeys: string[],
  options?: UseMultiLayerFeaturesOptions
): {
  features: Feature[];
  isLoading: boolean;
  isError: boolean;
} => {
  const { featuresByLayer, isLoading, isError } = useFeaturesByLayer(versionId, layerKeys);

  // Tag each feature with properties.layerId so features from different layers
  // can be distinguished when later saved back as a single combined collection.
  const features = useMemo(() => {
    const all: Feature[] = [];
    Object.entries(featuresByLayer).forEach(([layerKey, layerFeatures]) => {
      const targetLayerId = options?.layerIdMapping?.[layerKey] ?? layerKey;
      layerFeatures.forEach((f) => {
        all.push({
          ...f,
          properties: { ...f.properties, layerId: targetLayerId },
        });
      });
    });
    return all;
  }, [featuresByLayer, options?.layerIdMapping]);

  return { features, isLoading, isError };
};
